/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';

import {
    CountryPicker,
    FlexCol,
    FlexRow,
    StatePicker,
    TextField,
    ZIPCodeField,
    Heading,
    Spacer,
    Alert,
} from 'sarsaparilla';

function MailingAddressForm(props) {
    const {
        mailingInfo,
        number,
        onChange,
        skipCustomerInfo,
        submitAttempted,
        verifyAddress,
    } = props;
    const [localAddress, setLocalAddress] = useState({});
    const [isValidAddress, setIsValidAddress] = useState(undefined);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (
                !localAddress.address_1 ||
                !localAddress.city ||
                !localAddress.state ||
                !localAddress.zip_code
            ) {
                return;
            }

            verifyAddress({
                address1: localAddress.address_1,
                state: localAddress.state,
                city: localAddress.city,
                zip_code: localAddress.zip_code,
            })
                .then((result) => {
                    setIsValidAddress(result.zip_code.includes(localAddress.zip_code));
                })
                .catch(() => {
                    setIsValidAddress(false);
                });
        }, 2000);
        return () => {
            clearTimeout(timer);
        };
    }, [localAddress, verifyAddress]);

    const handleChange = (key, value) => {
        const mailingInfoValues = set(cloneDeep(mailingInfo), key, value);
        let mailingInfoIsValid = false;

        if (
            mailingInfoValues?.address_1 &&
            mailingInfoValues?.city &&
            mailingInfoValues?.zip_code &&
            mailingInfoValues?.country
        ) {
            mailingInfoIsValid = true;
        }

        if (key === 'country' && value !== 'USA') {
            mailingInfo.state = '';
        }

        onChange(key, value, mailingInfoIsValid);

        if (
            mailingInfoIsValid &&
            mailingInfoValues?.country === 'USA' &&
            (mailingInfoValues?.state !== localAddress.state ||
                mailingInfoValues?.city !== localAddress.city ||
                mailingInfoValues?.zip_code !== localAddress.zip_code ||
                mailingInfoValues?.address_1 !== localAddress.address_1)
        ) {
            setLocalAddress(mailingInfoValues);
        }
    };

    return (
        <div>
            <Spacer />
            <Heading headingLevel={3} appearance="h6" className="mb-2">
                Mailing Address
            </Heading>
            <FlexRow>
                <FlexCol>
                    <Alert type="info" shouldFocusOnMount className="mb-1" hasAriaLive>
                        Please enter a <strong>valid USPS mailing address</strong> to
                        ensure your passes are shipped correctly. Double-check the
                        spelling, include any apartment or suite numbers, and confirm that
                        your ZIP code is accurate. Orders cannot be delivered if the
                        address is incomplete or incorrect.
                    </Alert>
                </FlexCol>
            </FlexRow>
            <FlexRow>
                <FlexCol lg={6}>
                    <div className="rec-form-item-wrap">
                        <CountryPicker
                            id={`country-${number}`}
                            value={skipCustomerInfo ? '' : mailingInfo?.country}
                            isDisabled={skipCustomerInfo}
                            onChange={(e) => handleChange('country', e.target.value)}
                            isRequired={!skipCustomerInfo}
                            isInvalid={submitAttempted && !mailingInfo?.country}
                        />
                    </div>
                </FlexCol>
            </FlexRow>
            <FlexRow>
                <FlexCol lg={6}>
                    <div className="rec-form-item-wrap">
                        <TextField
                            maxLength={100}
                            label="Address Line 1"
                            id={`address-line-1-${number}`}
                            value={skipCustomerInfo ? '' : mailingInfo?.address_1}
                            isRequired={!skipCustomerInfo}
                            isDisabled={skipCustomerInfo}
                            shouldValidateOnUpdate={submitAttempted}
                            onChange={(e) => handleChange('address_1', e.target.value)}
                        />
                    </div>
                </FlexCol>
                <FlexCol lg={6}>
                    <div className="rec-form-item-wrap">
                        <TextField
                            maxLength={100}
                            label="Address Line 2"
                            id={`address-line-2-${number}`}
                            value={skipCustomerInfo ? '' : mailingInfo?.address_2}
                            isDisabled={skipCustomerInfo}
                            shouldValidateOnUpdate={submitAttempted}
                            onChange={(e) => handleChange('address_2', e.target.value)}
                        />
                    </div>
                </FlexCol>
            </FlexRow>
            <FlexRow>
                <FlexCol lg={6}>
                    <div className="rec-form-item-wrap">
                        <TextField
                            maxLength={100}
                            label="City / Town"
                            id={`city-${number}`}
                            value={skipCustomerInfo ? '' : mailingInfo?.city}
                            isRequired={!skipCustomerInfo}
                            isDisabled={skipCustomerInfo}
                            shouldValidateOnUpdate={submitAttempted}
                            onChange={(e) => handleChange('city', e.target.value)}
                        />
                    </div>
                </FlexCol>
                <FlexCol lg={3}>
                    <div className="rec-form-item-wrap">
                        <StatePicker
                            id={`state-${number}`}
                            label="State (US only)"
                            isDisabled={
                                skipCustomerInfo || mailingInfo?.country !== 'USA'
                            }
                            isRequired={
                                !skipCustomerInfo && mailingInfo?.country === 'USA'
                            }
                            value={skipCustomerInfo ? '' : mailingInfo?.state}
                            onChange={(e) => handleChange('state', e.target.value)}
                        />
                    </div>
                </FlexCol>
                <FlexCol lg={3}>
                    <div className="rec-form-item-wrap">
                        <ZIPCodeField
                            id={`zip-code-${number}`}
                            value={skipCustomerInfo ? '' : mailingInfo?.zip_code}
                            isRequired={!skipCustomerInfo}
                            isDisabled={skipCustomerInfo}
                            shouldValidateOnUpdate={submitAttempted}
                            onChange={(e) => handleChange('zip_code', e.target.value)}
                            label="ZIP Code"
                        />
                    </div>
                </FlexCol>
            </FlexRow>
            <div aria-live="polite">
                {isValidAddress === false && (
                    <Alert
                        type={'warning'}
                        className="mt-2 mb-1"
                        shouldFocusOnMount
                        hasAriaLive
                    >
                        We were unable to verify your address with USPS. You can still
                        proceed, but please double-check your information to avoid delayed
                        or missing shipments.
                    </Alert>
                )}
            </div>
        </div>
    );
}

MailingAddressForm.propTypes = {
    mailingInfo: PropTypes.object.isRequired,
    number: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    verifyAddress: PropTypes.func,
    submitAttempted: PropTypes.bool.isRequired,
    skipCustomerInfo: PropTypes.bool,
};

export default MailingAddressForm;
